/* Vista a elenco delle disponibilità (backoffice), raggruppabile per giorno o per studio,
   con filtri per medico, tipologia di servizio, stato e periodo. */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { CalendarDays, Building2, Stethoscope, Clock, ClipboardList, Plus, Trash2, Unlock } from 'lucide-react';
import logger from '@/lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GIORNI = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];

const fmtDate = (iso) => {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return `${GIORNI[d.getDay()]} ${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  } catch {
    return iso;
  }
};

const StatusBadge = ({ status }) => status === 'available'
  ? <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))]">Libero</Badge>
  : <Badge className="bg-slate-500 text-white">Prenotato</Badge>;

const SlotRow = ({ slot, showDate, showStudio, onDelete, onFree }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2 px-3 border-b last:border-b-0 hover:bg-[hsl(var(--secondary))]/60 transition-colors duration-150" data-testid={`avail-slot-row-${slot.id}`}>
    {showDate && <span className="text-sm font-medium w-44 flex-shrink-0">{fmtDate(slot.data)}</span>}
    <span className="text-sm flex items-center gap-1 w-28 flex-shrink-0"><Clock size={13} className="text-muted-foreground" />{slot.ora_inizio}{slot.ora_fine ? `–${slot.ora_fine}` : ''}</span>
    <span className="text-sm flex items-center gap-1 flex-1 min-w-40"><Stethoscope size={13} className="text-[#238A92]" />Dott. {slot.doctor_nome} {slot.doctor_cognome}</span>
    {showStudio && (
      <span className="text-xs text-muted-foreground flex items-center gap-1 w-40">
        <Building2 size={12} />{slot.studio_nome || 'Studio non assegnato'}
      </span>
    )}
    <span className="text-xs text-muted-foreground w-16">{slot.durata_minuti || 30} min</span>
    <StatusBadge status={slot.status} />
    {slot.status === 'available' ? (
      <Button
        variant="ghost" size="sm"
        className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
        title="Elimina questa disponibilità"
        onClick={() => onDelete(slot)}
        data-testid={`avail-slot-delete-${slot.id}`}
      >
        <Trash2 size={14} />
      </Button>
    ) : (
      <Button
        variant="ghost" size="sm"
        className="h-7 px-2 text-amber-700 hover:text-amber-800 hover:bg-amber-50 gap-1"
        title="Libera lo slot: annulla l'appuntamento, avvisa il paziente e crea una ricollocazione"
        onClick={() => onFree(slot)}
        data-testid={`avail-slot-free-${slot.id}`}
      >
        <Unlock size={14} /> <span className="text-xs">Libera</span>
      </Button>
    )}
  </div>
);

export const AvailabilityListView = ({ doctors }) => {
  const [slots, setSlots] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [days, setDays] = useState('14');
  const [groupBy, setGroupBy] = useState('giorno');
  const [studios, setStudios] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ doctor_id: '', data: '', ora_inizio: '09:00', durata_minuti: 30, studio_id: 'none' });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [freeTarget, setFreeTarget] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then(r => setServices(r.data.filter(s => s.active !== false)))
      .catch(e => logger.warn('Servizi non caricati:', e?.message));
    axios.get(`${API}/studios`)
      .then(r => setStudios(r.data.filter(s => s.active !== false)))
      .catch(e => logger.warn('Studi non caricati:', e?.message));
  }, []);

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (doctorFilter !== 'all') params.doctor_id = doctorFilter;
      if (serviceFilter !== 'all') params.service_id = serviceFilter;
      if (statusFilter !== 'all') params.slot_status = statusFilter;
      const res = await axios.get(`${API}/admin/availability-list`, { params });
      setSlots(res.data.slots || []);
    } catch (err) {
      logger.error('Availability list error:', err);
    } finally {
      setLoading(false);
    }
  }, [doctorFilter, serviceFilter, statusFilter, days]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const openAdd = () => {
    const today = new Date().toISOString().slice(0, 10);
    const firstDoc = doctorFilter !== 'all' ? doctorFilter : (doctors[0]?.id || '');
    const doc = doctors.find(d => d.id === firstDoc);
    setAddForm({ doctor_id: firstDoc, data: today, ora_inizio: '09:00', durata_minuti: doc?.durata_slot_minuti || 30, studio_id: 'none' });
    setAddOpen(true);
  };

  const handleAddDoctorChange = (docId) => {
    const doc = doctors.find(d => d.id === docId);
    setAddForm(p => ({ ...p, doctor_id: docId, durata_minuti: doc?.durata_slot_minuti || p.durata_minuti }));
  };

  const submitAdd = async () => {
    if (!addForm.doctor_id || !addForm.data || !addForm.ora_inizio) {
      toast.error('Compila dottore, data e ora di inizio');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        doctor_id: addForm.doctor_id,
        data: addForm.data,
        ora_inizio: addForm.ora_inizio,
        durata_minuti: parseInt(addForm.durata_minuti, 10) || 30,
      };
      if (addForm.studio_id && addForm.studio_id !== 'none') payload.studio_id = addForm.studio_id;
      await axios.post(`${API}/availability-slots`, payload);
      toast.success('Slot aggiunto');
      setAddOpen(false);
      fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione dello slot');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/availability-slots/${deleteTarget.id}`);
      toast.success('Disponibilità eliminata');
      fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eliminazione fallita');
    } finally { setDeleteTarget(null); }
  };

  const confirmFree = async () => {
    if (!freeTarget) return;
    try {
      const res = await axios.post(`${API}/availability-slots/${freeTarget.id}/free`);
      toast.success(res.data?.appointment_cancelled
        ? 'Slot liberato: appuntamento annullato, paziente avvisato e ricollocazione creata'
        : 'Slot liberato');
      fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione fallita');
    } finally { setFreeTarget(null); }
  };

  // Raggruppamento
  const groups = (() => {
    const map = new Map();
    for (const s of slots) {
      const key = groupBy === 'giorno'
        ? s.data
        : (s.studio_nome || 'Studio non assegnato');
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    const entries = [...map.entries()];
    if (groupBy === 'giorno') entries.sort((a, b) => a[0].localeCompare(b[0]));
    else entries.sort((a, b) => a[0].localeCompare(b[0], 'it'));
    return entries;
  })();

  const freeCount = slots.filter(s => s.status === 'available').length;

  return (
    <div className="space-y-4">
      {/* Filtri */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Dottore</Label>
            <Select value={doctorFilter} onValueChange={setDoctorFilter}>
              <SelectTrigger className="w-56" data-testid="avail-list-doctor-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i dottori</SelectItem>
                {doctors.map(d => <SelectItem key={d.id} value={d.id}>Dott. {d.nome} {d.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Tipologia di servizio</Label>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger className="w-64" data-testid="avail-list-service-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i servizi</SelectItem>
                {services.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Stato</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="avail-list-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="available">Liberi</SelectItem>
                <SelectItem value="booked">Prenotati</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Periodo</Label>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-40" data-testid="avail-list-days-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Prossimi 7 giorni</SelectItem>
                <SelectItem value="14">Prossimi 14 giorni</SelectItem>
                <SelectItem value="30">Prossimi 30 giorni</SelectItem>
                <SelectItem value="60">Prossimi 60 giorni</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 ml-auto">
            <Label className="text-xs">Raggruppa per</Label>
            <ToggleGroup type="single" value={groupBy} onValueChange={v => v && setGroupBy(v)} className="border rounded-md">
              <ToggleGroupItem value="giorno" className="gap-1.5 px-3 data-[state=on]:bg-[#238A92] data-[state=on]:text-white" data-testid="avail-group-by-day">
                <CalendarDays size={14} /> Giorno
              </ToggleGroupItem>
              <ToggleGroupItem value="studio" className="gap-1.5 px-3 data-[state=on]:bg-[#238A92] data-[state=on]:text-white" data-testid="avail-group-by-studio">
                <Building2 size={14} /> Studio
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <Button onClick={openAdd} className="bg-[#238A92] hover:bg-[#0F5E66] gap-1.5" data-testid="avail-add-slot-button">
            <Plus size={15} /> Aggiungi slot
          </Button>
        </CardContent>
      </Card>

      {/* Riepilogo */}
      {!loading && (
        <p className="text-sm text-muted-foreground" data-testid="avail-list-summary">
          {slots.length} slot nel periodo — <span className="text-[hsl(var(--success))] font-medium">{freeCount} liberi</span>, {slots.length - freeCount} prenotati
        </p>
      )}

      {/* Elenco raggruppato */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ClipboardList size={36} className="mx-auto text-[#238A92] mb-3" />
            <p className="font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Nessuno slot trovato</p>
            <p className="text-sm text-muted-foreground mt-1">Prova ad ampliare il periodo o a rimuovere qualche filtro.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map(([key, items]) => (
            <Card key={key} data-testid={`avail-group-${key}`}>
              <div className="px-4 py-2.5 border-b bg-[hsl(var(--secondary))]/50 flex items-center justify-between rounded-t-xl">
                <p className="font-semibold text-sm flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                  {groupBy === 'giorno' ? <CalendarDays size={15} className="text-[#0F5E66]" /> : <Building2 size={15} className="text-[#0F5E66]" />}
                  {groupBy === 'giorno' ? fmtDate(key) : key}
                </p>
                <span className="text-xs text-muted-foreground">{items.length} slot — {items.filter(s => s.status === 'available').length} liberi</span>
              </div>
              <CardContent className="p-0">
                {items.map(s => (
                  <SlotRow key={s.id} slot={s} showDate={groupBy === 'studio'} showStudio={groupBy === 'giorno'} onDelete={setDeleteTarget} onFree={setFreeTarget} />
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog: Aggiungi slot */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Aggiungi slot di disponibilità</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Dottore *</Label>
              <Select value={addForm.doctor_id} onValueChange={handleAddDoctorChange}>
                <SelectTrigger data-testid="add-slot-doctor"><SelectValue placeholder="Seleziona dottore" /></SelectTrigger>
                <SelectContent>
                  {doctors.map(d => <SelectItem key={d.id} value={d.id}>Dott. {d.nome} {d.cognome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Data *</Label>
                <Input type="date" value={addForm.data} min={new Date().toISOString().slice(0, 10)} onChange={e => setAddForm(p => ({ ...p, data: e.target.value }))} data-testid="add-slot-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Ora inizio *</Label>
                <Input type="time" value={addForm.ora_inizio} onChange={e => setAddForm(p => ({ ...p, ora_inizio: e.target.value }))} data-testid="add-slot-time" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Durata (minuti)</Label>
                <Input type="number" min={5} max={240} step={5} value={addForm.durata_minuti} onChange={e => setAddForm(p => ({ ...p, durata_minuti: e.target.value }))} data-testid="add-slot-duration" />
              </div>
              <div className="space-y-1.5">
                <Label>Studio / stanza (opzionale)</Label>
                <Select value={addForm.studio_id} onValueChange={v => setAddForm(p => ({ ...p, studio_id: v }))}>
                  <SelectTrigger data-testid="add-slot-studio"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuno (assegnato dopo)</SelectItem>
                    {studios.map(s => <SelectItem key={s.id} value={s.id}>{s.nome}{s.numero ? ` (${s.numero})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annulla</Button>
            <Button onClick={submitAdd} disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66]" data-testid="add-slot-submit">
              {saving ? 'Salvataggio...' : 'Aggiungi slot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conferma eliminazione slot libero */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa disponibilità?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && `Dott. ${deleteTarget.doctor_nome} ${deleteTarget.doctor_cognome} — ${fmtDate(deleteTarget.data)} ore ${deleteTarget.ora_inizio}. Lo slot non sarà più prenotabile.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-slot">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conferma liberazione slot prenotato */}
      <AlertDialog open={!!freeTarget} onOpenChange={open => !open && setFreeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Liberare questo slot prenotato?</AlertDialogTitle>
            <AlertDialogDescription>
              {freeTarget && `Dott. ${freeTarget.doctor_nome} ${freeTarget.doctor_cognome} — ${fmtDate(freeTarget.data)} ore ${freeTarget.ora_inizio}. `}
              L'appuntamento collegato sarà <strong>annullato</strong>: il paziente verrà avvisato (email/WhatsApp secondo i canali configurati) e verrà creata una <strong>ricollocazione</strong> nel pannello dedicato. Lo slot tornerà libero e prenotabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmFree} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-free-slot">Libera slot</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
